import React, { useState, useEffect } from 'react';
import { useWarehouses } from '../../hooks/useWarehouses';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  MapPinIcon,
  BuildingStorefrontIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import LoadingScreen from '../common/LoadingScreen';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import toast from 'react-hot-toast';
import { WarehouseLocation } from '../../types/warehouse';

interface FormData {
  name: string;
  code: string;
  description: string;
  isActive: boolean;
}

const WarehouseLocationList: React.FC = () => {
  const {
    warehouses,
    warehouseLocations,
    loading,
    fetchLocationsForWarehouse,
    createWarehouseLocation,
    updateWarehouseLocation,
    deleteWarehouseLocation
  } = useWarehouses();
  const { t } = useLanguage();
  const [expandedWarehouse, setExpandedWarehouse] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<WarehouseLocation | null>(null);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [form, setForm] = useState<FormData>({ name: '', code: '', description: '', isActive: true });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<string | null>(null);

  useEffect(() => {
    if (warehouses.length > 0 && warehouseLocations.length === 0) {
      warehouses.forEach(w => fetchLocationsForWarehouse(w.id));
    }
  }, [warehouses, warehouseLocations.length, fetchLocationsForWarehouse]);

  const openCreate = (warehouseId: string) => {
    setSelectedWarehouseId(warehouseId);
    setEditingLocation(null);
    setForm({ name: '', code: '', description: '', isActive: true });
    setIsModalOpen(true);
  };

  const openEdit = (loc: WarehouseLocation) => {
    setSelectedWarehouseId(loc.warehouseId);
    setEditingLocation(loc);
    setForm({
      name: loc.name,
      code: loc.code,
      description: loc.description || '',
      isActive: loc.isActive
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await updateWarehouseLocation(editingLocation.id, {
          ...form,
          warehouseId: selectedWarehouseId
        });
        toast.success('Raktárhely frissítve');
      } else {
        await createWarehouseLocation({
          warehouseId: selectedWarehouseId,
          name: form.name,
          code: form.code,
          description: form.description,
          isActive: form.isActive
        });
        toast.success('Raktárhely létrehozva');
      }
      setIsModalOpen(false);
    } catch (err) {
      toast.error('Hiba történt');
    }
  };

  const openDelete = (id: string) => {
    setLocationToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!locationToDelete) return;
    try {
      await deleteWarehouseLocation(locationToDelete);
      toast.success('Raktárhely törölve');
    } catch (err) {
      toast.error('Hiba a törléskor');
    } finally {
      setIsDeleteModalOpen(false);
      setLocationToDelete(null);
    }
  };

  if (loading) return <LoadingScreen />;

  const grouped = warehouses.map(wh => ({
    ...wh,
    locations: warehouseLocations.filter(loc => loc.warehouseId === wh.id)
  }));

  return (
    <div className="p-4 space-y-4">
      {grouped.length === 0 ? (
        <div className="text-center py-10 text-gray-500 dark:text-gray-400">
          {t('warehouse.list.empty')}
        </div>
      ) : (
        grouped.map(wh => (
          <div key={wh.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
            <button
              onClick={() => setExpandedWarehouse(expandedWarehouse === wh.id ? null : wh.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left"
            >
              <div className="flex items-center">
                <BuildingStorefrontIcon className="h-5 w-5 text-primary-500 mr-3" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">{wh.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{wh.code} · {wh.location}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {wh.locations.length} raktárhely
                </span>
                {expandedWarehouse === wh.id ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                )}
              </div>
            </button>

            {expandedWarehouse === wh.id && (
              <div className="border-t border-gray-200 dark:border-gray-600">
                <div className="p-3 flex justify-end">
                  <button
                    onClick={() => openCreate(wh.id)}
                    className="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    {t('warehouse.locations.create')}
                  </button>
                </div>

                {wh.locations.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                    {t('warehouse.locations.empty')}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4 pt-0">
                    {wh.locations.map(loc => (
                      <div
                        key={loc.id}
                        className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-600 p-3 flex items-start justify-between group"
                      >
                        <div className="flex items-start min-w-0">
                          <MapPinIcon className="h-4 w-4 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                              {loc.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Kód: {loc.code}
                            </div>
                            {loc.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                                {loc.description}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => openEdit(loc)}
                            className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                            title={t('warehouse.locations.edit')}
                          >
                            <PencilIcon className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => openDelete(loc.id)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title={t('warehouse.locations.delete')}
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))
      )}

      {/* Delete Confirm Modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setLocationToDelete(null); }}
        onConfirm={handleDeleteConfirm}
        title={t('warehouse.locations.delete')}
        deleteMessage={t('warehouse.locations.deleteConfirmation')}
        deleteWarning="A művelet nem vonható vissza."
      />

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {editingLocation ? t('warehouse.locations.edit') : t('warehouse.locations.create')}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Név
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kód
                </label>
                <input
                  type="text"
                  value={form.code}
                  onChange={e => setForm({ ...form, code: e.target.value })}
                  required
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Megjegyzés
                </label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={form.isActive}
                  onChange={e => setForm({ ...form, isActive: e.target.checked })}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Aktív
                </label>
              </div>
              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                >
                  {editingLocation ? t('common.save') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseLocationList;
